import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Info,
  Library,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { Field } from "@/components/forms";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useDiscoverCourses, useImportCourse } from "@/hooks/useAdminData";
import { GridSkeleton } from "@/components/shared/Skeletons";

export const Route = createFileRoute("/admin/courses/discovery")({
  component: CourseDiscoveryPage,
});

type ExternalCourse = {
	id: string;
	title: string;
	description: string;
	instructor: string;
	duration: string;
	lessons_count: number;
	thumbnail: string;
	external_content_url: string;
	category: string;
	tags: string[];
	preview_url: string;
};

function CourseDiscoveryPage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState<ExternalCourse | null>(null);
  const [openImportDialog, setOpenImportDialog] = useState(false);

  // Form states
  const [visibility, setVisibility] = useState("published");
  const [pricingType, setPricingType] = useState("free");
  const [price, setPrice] = useState("0");
  const [customDescription, setCustomDescription] = useState("");
  const [importedIds, setImportedIds] = useState<string[]>([]);

  // Fetch discovered courses
  const { data: discoveredCourses = [], isLoading } = useDiscoverCourses(
    search,
    category === "all" ? "" : category
  );

  const [page, setPage] = useState(1);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [search, category]);

  const itemsPerPage = 6;
  const pageCount = Math.ceil(discoveredCourses.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedCourses = discoveredCourses.slice(
    startIndex,
    startIndex + itemsPerPage,
  ) as ExternalCourse[];

  const importCourse = useImportCourse();

  const handleOpenImport = (course: ExternalCourse) => {
    setSelectedCourse(course);
    setCustomDescription(course.description);
    setOpenImportDialog(true);
  };

  const handleConfirmImport = () => {
    if (!selectedCourse) return;

    importCourse.mutate(
      {
        external_id: selectedCourse.id,
        visibility,
        pricing_type: pricingType,
        price: parseFloat(price) || 0,
        custom_description: customDescription,
      },
      {
        onSuccess: () => {
          setImportedIds((prev) => [...prev, selectedCourse.id]);
          setOpenImportDialog(false);
          setSelectedCourse(null);
        },
        onError: (err) => {
          alert("Failed to import course: " + err.message);
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="External course discovery"
        subtitle="Search and import curriculum from our external learning provider network directly into our student catalog."
        breadcrumbs={[
          { label: "Dashboard", path: "/admin/dashboard" },
          { label: "Courses", path: "/admin/courses" },
          { label: "Discovery" },
        ]}
        action={
          <Button variant="outline" asChild>
            <Link to="/admin/courses">
              <ArrowLeft />
              Back to catalog
            </Link>
          </Button>
        }
      />

      <SectionCard title="Curate external catalog">
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 md:items-end">
          <Field label="Search" className="md:col-span-2">
            {({ id }) => (
              <SearchInput
                id={id}
                className="sm:w-full"
                value={search}
                onChange={setSearch}
                placeholder="Search provider courses (e.g. Cisco, Word, Python)…"
              />
            )}
          </Field>
          <Field label="Discipline / category">
            {({ id }) => (
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id={id} className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All disciplines</SelectItem>
                  <SelectItem value="IT & Tech">IT & Tech</SelectItem>
                  <SelectItem value="Cybersecurity & Networking">Cybersecurity & Networking</SelectItem>
                  <SelectItem value="Business & Productivity">Business & Productivity</SelectItem>
                  <SelectItem value="Design & Marketing">Design & Marketing</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>
      </SectionCard>

      {isLoading ? (
        <GridSkeleton cards={6} />
      ) : discoveredCourses.length === 0 ? (
        <EmptyState
          icon={<Library />}
          title="No courses found on provider"
          description="Try adjusting your search terms or selecting another category."
        />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {paginatedCourses.map((c: ExternalCourse) => {
              const isAlreadyImported = importedIds.includes(c.id);
              return (
                <article
                  key={c.id}
                  className="flex h-full flex-col overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-hairline-strong"
                >
                  <img
                    src={c.thumbnail}
                    alt={c.title}
                    className="h-36 w-full border-b border-border bg-muted object-cover"
                    loading="lazy"
                  />
                  <div className="flex flex-1 flex-col p-4">
                    <Badge variant="outline" className="mb-3 self-start">
                      {c.category}
                    </Badge>
                    <h3 className="mb-1.5 line-clamp-2 min-h-10 text-[15px] leading-5 font-medium text-foreground">
                      {c.title}
                    </h3>
                    <p className="mb-4 line-clamp-3 flex-1 text-caption text-muted-foreground">
                      {c.description}
                    </p>
                    <div className="mb-4 flex flex-wrap gap-4 border-t border-border pt-3 text-caption text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="size-3.5" aria-hidden />
                        {c.duration}
                      </span>
                      <span className="inline-flex items-center gap-1.5 tabular-nums">
                        <BookOpen className="size-3.5" aria-hidden />
                        {c.lessons_count} lessons
                      </span>
                    </div>
                    <Button
                      variant={isAlreadyImported ? "secondary" : "outline"}
                      className="w-full"
                      disabled={isAlreadyImported}
                      onClick={() => handleOpenImport(c)}
                    >
                      {isAlreadyImported ? <CheckCircle2 /> : <Download />}
                      {isAlreadyImported ? "Imported" : "Import to platform"}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>

          {pageCount > 1 && (
            <nav aria-label="Pagination" className="flex items-center justify-center gap-1">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </Button>
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                <Button
                  key={p}
                  variant={p === page ? "secondary" : "ghost"}
                  size="icon-sm"
                  onClick={() => setPage(p)}
                  aria-current={p === page ? "page" : undefined}
                  aria-label={`Page ${p}`}
                  className={cn("tabular-nums", p !== page && "text-muted-foreground")}
                >
                  {p}
                </Button>
              ))}
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => setPage(page + 1)}
                disabled={page >= pageCount}
                aria-label="Next page"
              >
                <ChevronRight />
              </Button>
            </nav>
          )}
        </div>
      )}

      <Dialog open={openImportDialog} onOpenChange={(o) => !o && setOpenImportDialog(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import & curation settings</DialogTitle>
            <DialogDescription>Choose how this course appears in the student catalog.</DialogDescription>
          </DialogHeader>
          {selectedCourse && (
            <div className="flex flex-col gap-4">
              <Alert variant="info">
                <Info />
                <AlertDescription>
                  You are importing <strong className="font-medium">{selectedCourse.title}</strong>{" "}
                  from FreeCourseSites. Content links will be dynamic and styled natively.
                </AlertDescription>
              </Alert>

              <Field label="Visibility status">
                {({ id }) => (
                  <Select value={visibility} onValueChange={setVisibility}>
                    <SelectTrigger id={id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft (Hidden from Catalog)</SelectItem>
                      <SelectItem value="published">Published (Visible immediately)</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Pricing type">
                  {({ id }) => (
                    <Select value={pricingType} onValueChange={setPricingType}>
                      <SelectTrigger id={id}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free Course</SelectItem>
                        <SelectItem value="paid">Paid Track</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Course price (TZS)">
                  <Input
                    disabled={pricingType === "free"}
                    type="number"
                    value={pricingType === "free" ? "0" : price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g. 50000"
                    className="tabular-nums"
                  />
                </Field>
              </div>

              <Field label="Custom description (optional)">
                <Textarea
                  rows={3}
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  placeholder="Add platform-specific descriptions if desired"
                />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenImportDialog(false)}>
              Cancel
            </Button>
            <Button loading={importCourse.isPending} onClick={handleConfirmImport}>
              {importCourse.isPending ? "Importing…" : "Confirm import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
